import * as Sentry from '@sentry/vue'
import config from '@/config'
import util from '@/util'

const openChat = () => {
  if (window.isZohoReady && window.$zoho) {
    // https://www.zoho.com/salesiq/help/developer-section/js-api-chat-start.html
    try {
      window.$zoho.salesiq.chat.start()
    } catch (e) {
      Sentry.captureException(e)
    }
  }
}

const hideChatBtn = () => {
  if (window.isZohoReady && window.$zoho) {
    try {
      window.$zoho.salesiq.chatbutton.visible('hide')
      window.$zoho.salesiq.floatbutton.visible('hide')
      window.$zoho.salesiq.chatwindow.visible('hide')
      window.$zoho.salesiq.floatwindow.visible('hide')
    } catch (e) {
      Sentry.captureException(e)
    }
  }
}

const showChatBtn = () => {
  if (window.isZohoReady && window.$zoho) {
    try {
      window.$zoho.salesiq.floatbutton.visible('show')
    } catch (e) {
      Sentry.captureException(e)
    }
  }
}

/**
 * The exact inverse of hideChatBtn().
 *
 * showChatBtn() only re-shows `floatbutton`, so it does NOT undo hideChatBtn() —
 * `chatbutton`, `chatwindow` and `floatwindow` stay hidden. That asymmetry is
 * harmless for the legacy callers, which hide/show around a transient in-editor
 * state, but it breaks the tool-bridge flag as a KILL SWITCH: flipping the flag
 * off at runtime has to make support chat fully reachable again without a reload.
 *
 * Kept separate from showChatBtn() on purpose — changing showChatBtn() itself
 * would alter the legacy (flag-off) paths in Middle.vue / EmbedAppContainer.vue.
 */
const restoreChatBtn = () => {
  if (window.isZohoReady && window.$zoho) {
    try {
      window.$zoho.salesiq.chatbutton.visible('show')
      window.$zoho.salesiq.floatbutton.visible('show')
      window.$zoho.salesiq.chatwindow.visible('show')
      window.$zoho.salesiq.floatwindow.visible('show')
    } catch (e) {
      Sentry.captureException(e)
    }
  }
}

const identifyUser = (
  {
    id,
    email,
    plan,
    firstName,
    lastName,
  },
) => {
  if (!window.isZohoReady || !window.$zoho || !window.$zoho.salesiq) {
    // if zoho not ready yet, save for later
    window.zohoCustomerInfo = {
      id,
      email,
      plan,
      firstName,
      lastName,
    }
    return
  }
  try {
    if (firstName || lastName) {
      window.$zoho.salesiq.visitor.name(`${firstName} ${lastName}`)
    }
    if (email) {
      window.$zoho.salesiq.visitor.email(email)
    }
    window.$zoho.salesiq.visitor.info({
      Description: `UserId: ${id}`,
      Plan: plan,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

// office.js nulls history.pushState/replaceState on load, and the SalesIQ widget calls
// replaceState during its own boot, which can run before services/office restores them.
const restoreHistoryNulledByOfficeJs = () => {
  const historyCache = window._historyCache
  if (!historyCache) return
  ['replaceState', 'pushState', 'back'].forEach((method) => {
    if (typeof window.history[method] !== 'function' && typeof historyCache[method] === 'function') {
      window.history[method] = historyCache[method]
    }
  })
}

const init = () => {
  const isEU = config.region === 'eu'
  const isWhiteLabel = util.checkWhiteLabelDomain()

  const ZOHO_SCRIPT_ID = 'zsiqscript'
  if (document.getElementById(ZOHO_SCRIPT_ID) || isEU || isWhiteLabel || config.isEnableAddinPowerpoint) {
    return
  }
  try {
    restoreHistoryNulledByOfficeJs()
    window.$zoho = window.$zoho || {}
    window.$zoho.salesiq = window.$zoho.salesiq || {
      widgetcode: 'siq638b670686866584f0086fbe033f57ec93f5090a9233b2f8097d4874a025be64',
      values: {},
      ready() {
        // AHA-8833, handle when zoho not loaded yet
        window.isZohoReady = true
        identifyUser(window.zohoCustomerInfo)
      },
    }
    const d = document
    const s = d.createElement('script')
    s.type = 'text/javascript'
    s.id = ZOHO_SCRIPT_ID
    s.defer = true
    s.src = 'https://salesiq.zohopublic.com/widget?wc=siq638b670686866584f0086fbe033f57ec93f5090a9233b2f8097d4874a025be64'
    const t = d.getElementsByTagName('script')[0]
    t.parentNode.insertBefore(s, t)
  } catch (e) {
    Sentry.captureException(e)
  }
}

export default {
  openChat,
  hideChatBtn,
  showChatBtn,
  restoreChatBtn,
  identifyUser,
  init,
}
